import { Box, Paper, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { NotificationChannelsListProps, CARD_BORDER_RADIUS } from '../types';
import { NotificationChannelCard } from './NotificationChannelCard';

/**
 * NotificationChannelsList - Container component for the Notifications tab.
 *
 * Renders:
 * - Header with title/subtitle and "Add Channel" button
 * - Empty state when no channels configured
 * - List of NotificationChannelCard components
 */
export function NotificationChannelsList({
  channels,
  onAddChannel,
  onEditChannel,
  onDeleteChannel,
  onTestChannel,
  onChannelVerified,
}: NotificationChannelsListProps) {
  const theme = useTheme();

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Notification Channels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure where alerts are sent and what they watch
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddChannel}
        >
          Add Channel
        </Button>
      </Box>

      {/* Empty State */}
      {channels.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: CARD_BORDER_RADIUS,
            p: 4,
            textAlign: 'center',
          }}
        >
          <NotificationsIcon
            sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notification channels configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a channel to start receiving alerts via email, Slack, or other
            integrations.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddChannel}
          >
            Add Your First Channel
          </Button>
        </Paper>
      ) : (
        /* Channel Cards */
        <Box>
          {channels.map((channel) => (
            <NotificationChannelCard
              key={channel.id}
              channel={channel}
              onEdit={onEditChannel}
              onDelete={onDeleteChannel}
              onTest={onTestChannel}
              onVerified={onChannelVerified}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default NotificationChannelsList;
